import { useEffect, useRef } from 'react';

const isSaveKey = key => key === 's' || key === 'S';
const isNewKey = key => key === 'n' || key === 'N';
const isOpenKey = key => key === 'o' || key === 'O';
const isPrintKey = key => key === 'p' || key === 'P';
const isEditKey = key => key === 'e' || key === 'E';
const isStatusKey = key => key === 'u' || key === 'U';
const isAssignKey = key => key === 'a' || key === 'A';
const isEmailKey = key => key === 'm' || key === 'M';

/**
 *
 * @param {object} param
 * @param {VoidFunction|null} param.onOpen
 * @param {VoidFunction|null} param.onSave
 * @param {VoidFunction|null} param.onNew
 * @param {VoidFunction|null} param.onPrint
 * @param {VoidFunction|null} param.onEdit
 * @param {VoidFunction|null} param.onDelete
 * @param {VoidFunction|null} param.onUpdateOrderStatus
 * @param {VoidFunction|null} param.onAssignTransaction
 * @param {VoidFunction|null} param.onSendEmail
 * @param {boolean} param.disableShortcuts
 */
const useKeyboardShortcuts = ({
  onOpen = null,
  onSave = null,
  onNew = null,
  onPrint = null,
  onEdit = null,
  onDelete = null,
  onUpdateOrderStatus = null,
  onAssignTransaction = null,
  onSendEmail = null,
  disableShortcuts = false
}) => {
  const isMac = navigator.userAgent.indexOf('Mac OS X') != -1;

  useEffect(() => {
    if (!onOpen) return;

    function callback(event) {
      if (disableShortcuts) return;
      const { ctrlKey, key, metaKey } = event;

      const macShortcutValid = isMac && metaKey && isOpenKey(key);
      const otherDevShortcutValid = !isMac && ctrlKey && isOpenKey(key);

      if (macShortcutValid || otherDevShortcutValid) {
        event.preventDefault();
        onOpen();
      }
    }

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [onOpen, disableShortcuts]);

  useEffect(() => {
    if (!onSave) return;

    function callback(event) {
      if (disableShortcuts) return;
      const { ctrlKey, key, metaKey } = event;

      const macShortcutValid = isMac && metaKey && isSaveKey(key);
      const otherDevShortcutValid = !isMac && ctrlKey && isSaveKey(key);

      if (macShortcutValid || otherDevShortcutValid) {
        event.preventDefault();
        onSave();
      }
    }

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [onSave, disableShortcuts]);

  useEffect(() => {
    if (!onNew) return;

    function callback(event) {
      if (disableShortcuts) return;
      const { ctrlKey, key, metaKey } = event;

      const macShortcutValid = isMac && metaKey && isNewKey(key);
      const otherDevShortcutValid = !isMac && ctrlKey && isNewKey(key);

      if (macShortcutValid || otherDevShortcutValid) {
        event.preventDefault();
        onNew();
      }
    }

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [onNew, disableShortcuts]);

  useEffect(() => {
    if (!onPrint) return;

    function callback(event) {
      if (disableShortcuts) return;

      const { ctrlKey, metaKey, shiftKey, key } = event;

      const macShortcutValid = isMac && metaKey && shiftKey && isPrintKey(key);

      const otherDevShortcutValid =
        !isMac && ctrlKey && shiftKey && isPrintKey(key);

      if (macShortcutValid || otherDevShortcutValid) {
        event.preventDefault();
        onPrint();
      }
    }

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [onPrint, disableShortcuts]);

  useEffect(() => {
    if (!onEdit) return;

    function callback(event) {
      if (disableShortcuts) return;

      const { ctrlKey, metaKey, shiftKey, key } = event;

      const macShortcutValid = isMac && metaKey && shiftKey && isEditKey(key);

      const otherDevShortcutValid =
        !isMac && ctrlKey && shiftKey && isEditKey(key);

      if (macShortcutValid || otherDevShortcutValid) {
        event.preventDefault();
        onEdit();
      }
    }

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [onEdit, disableShortcuts]);

  useEffect(() => {
    if (!onDelete) return;

    function callback(event) {
      if (disableShortcuts) return;

      const { key, metaKey, ctrlKey } = event;

      const macShortcut = metaKey && key === 'Backspace';
      const windowsShortcut = ctrlKey && key === 'Delete';

      if (macShortcut || windowsShortcut) {
        event.preventDefault();
        onDelete();
      }
    }

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [onDelete, disableShortcuts]);

  useEffect(() => {
    if (!onUpdateOrderStatus) return;

    function callback(event) {
      if (disableShortcuts) return;

      const { key, ctrlKey, metaKey } = event;

      // Ctrl + U (Windows/Linux) or Cmd + U (Mac)
      if ((ctrlKey || metaKey) && isStatusKey(key)) {
        // console.log('Ctrl + U (Windows/Linux) or Cmd + U (Mac)');
        event.preventDefault();
        onUpdateOrderStatus();
      }
    }

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [onUpdateOrderStatus, disableShortcuts]);

  useEffect(() => {
    if (!onAssignTransaction) return;

    function callback(event) {
      if (disableShortcuts) return;

      const { key, ctrlKey, metaKey } = event;

      // Ctrl A U (Windows/Linux) or Cmd + A (Mac)
      if ((ctrlKey || metaKey) && isAssignKey(key)) {
        // console.log('Ctrl + A (Windows/Linux) or Cmd + A (Mac)');
        event.preventDefault();
        onAssignTransaction();
      }
    }

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [onAssignTransaction, disableShortcuts]);

  useEffect(() => {
    if (!onSendEmail) return;

    function callback(event) {
      if (disableShortcuts) return;

      const { key, ctrlKey, metaKey } = event;

      // Ctrl A U (Windows/Linux) or Cmd + A (Mac)
      if ((ctrlKey || metaKey) && isEmailKey(key)) {
        // console.log('Ctrl + A (Windows/Linux) or Cmd + A (Mac)');
        event.preventDefault();
        onSendEmail();
      }
    }

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [onSendEmail, disableShortcuts]);
};

export default useKeyboardShortcuts;
